import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import {
  ORG_INTEGRATION_STATES,
  ORGANIZATION_TAXONOMY,
  type DashboardReadout,
  type OrganizationQueueItem,
  type OrganizationQueuePage,
} from "@sourceenergy/sidekick-contracts";
import {
  Activity,
  HardHat,
  ArrowRight,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Database,
  FileCheck2,
  Gauge,
  Layers3,
  LogOut,
  MessageSquare,
  Plus,
  Search,
  ShieldCheck,
  X,
  Users,
} from "lucide-react";
import { AuthGate } from "./components/AuthGate";
import { OrganizationForm } from "./components/OrganizationForm";
import { SmsInbox } from "./components/SmsInbox";
import { ToastProvider } from "./components/Toast";
import { api } from "./lib/api";
import { supabase } from "./lib/supabase";

const WorkingWarriors = lazy(() =>
  import("./components/WorkingWarriors").then((module) => ({
    default: module.WorkingWarriors,
  })),
);

const WorkforceOpportunity = lazy(() =>
  import("./components/WorkforceOpportunity").then((module) => ({
    default: module.WorkforceOpportunity,
  })),
);

type Route =
  | "command"
  | "nodes"
  | "organizations"
  | "evidence"
  | "sms"
  | "warriors"
  | "opportunity";
type OrganizationView = "directory" | "intake";

const routes = [
  { id: "command", label: "Overview", group: "Operate", icon: Gauge },
  { id: "nodes", label: "Infrastructure", group: "Register", icon: Activity },
  {
    id: "organizations",
    label: "Organizations",
    group: "Register",
    icon: Building2,
  },
  { id: "evidence", label: "Evidence", group: "Assure", icon: FileCheck2 },
  { id: "sms", label: "SMS", group: "Connect", icon: MessageSquare },
  {
    id: "opportunity",
    label: "Workforce opportunity",
    group: "Develop",
    icon: HardHat,
  },
  { id: "warriors", label: "Working Warriors", group: "Develop", icon: Users },
] as const;

function routeFromHash(): Route {
  const route = location.hash.slice(1).split("?")[0];
  return routes.some((item) => item.id === route)
    ? (route as Route)
    : "command";
}

export function Console({ session }: { session: Session }) {
  const [route, setRoute] = useState<Route>(routeFromHash);
  const [opportunityVisited, setOpportunityVisited] = useState(
    () => route === "opportunity",
  );
  useEffect(() => {
    if (route === "opportunity") setOpportunityVisited(true);
  }, [route]);
  const [data, setData] = useState<DashboardReadout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    try {
      setData(await api.dashboard(session.access_token, controller.signal));
    } catch (reason) {
      if (!(reason instanceof DOMException && reason.name === "AbortError")) {
        setError(
          reason instanceof Error
            ? reason.message
            : "SideKick data is unavailable.",
        );
      }
    } finally {
      setLoading(false);
    }
    return () => controller.abort();
  }, [session.access_token]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [route]);

  const activateRoute = (nextRoute: Route) => {
    const nextHash = `#${nextRoute}`;
    if (window.location.hash !== nextHash) window.location.hash = nextHash;
    setRoute(nextRoute);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const currentRoute = routes.find((item) => item.id === route) ?? routes[0];
  const isAdmin =
    Array.isArray(session.user.app_metadata?.sidekick_roles) &&
    session.user.app_metadata.sidekick_roles.includes("admin");
  return (
    <div className="app-shell">
      <aside className="nav-rail">
        <a href="#command" className="brand" aria-label="SideKick overview">
          <span className="brand-mark">
            S<span>K</span>
          </span>
          <span>
            <strong>SideKick</strong>
            <small>Governed execution</small>
          </span>
        </a>
        <nav aria-label="Primary">
          {routes.map(
            ({ id, label, group, icon: Icon }, index) =>
              (id !== "sms" || isAdmin) && (
                <div className="nav-item" key={id}>
                  {(index === 0 || routes[index - 1]?.group !== group) && (
                    <span className="nav-section-label">{group}</span>
                  )}
                  <a
                    href={`#${id}`}
                    className={route === id ? "active" : ""}
                    aria-current={route === id ? "page" : undefined}
                    aria-controls="route-view"
                    onClick={(event) => {
                      event.preventDefault();
                      activateRoute(id);
                    }}
                  >
                    <Icon size={18} /> <span>{label}</span>
                    {id === "organizations" && data && (
                      <small>
                        {data.counts.organizations.toLocaleString()}
                      </small>
                    )}
                  </a>
                </div>
              ),
          )}
        </nav>
        <div className="nav-footer">
          <div className="identity">
            <ShieldCheck size={18} />
            <span>
              <strong>{session.user.email}</strong>
              <small>Authenticated operator</small>
            </span>
          </div>
          <button
            className="icon-button"
            aria-label="Sign out"
            onClick={() => void supabase?.auth.signOut()}
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <main className="command-surface">
        <header className="topline">
          <div>
            <p className="eyebrow">
              SOURCEENERGY / SIDEKICK / {currentRoute.group}
            </p>
            <h1>{currentRoute.label}</h1>
          </div>
          <div className="system-state">
            <span className="pulse" /> Governed register
          </div>
        </header>
        {route !== "sms" &&
          route !== "warriors" &&
          route !== "opportunity" &&
          loading && (
            <div className="panel center-state" role="status">
              <span className="spinner" /> Loading governed status…
            </div>
          )}
        {route !== "sms" &&
          route !== "warriors" &&
          route !== "opportunity" &&
          error && (
            <div className="panel error-state" role="alert">
              <strong>Program register unavailable</strong>
              <p>{error}</p>
              <button
                className="button button--outline"
                onClick={() => void load()}
              >
                Retry
              </button>
            </div>
          )}
        <div id="route-view" className="route-view" aria-live="polite">
          <div hidden={route !== "sms"}>
            <SmsInbox token={session.access_token} isAdmin={isAdmin} />
          </div>
          {(route === "opportunity" || opportunityVisited) && (
            <div hidden={route !== "opportunity"}>
              <RouteBoundary route="opportunity">
                <Suspense
                  fallback={
                    <div className="panel center-state" role="status">
                      Loading workforce opportunity…
                    </div>
                  }
                >
                  <WorkforceOpportunity
                    key={session.user.id}
                    active={route === "opportunity"}
                    intakeEnabled={
                      import.meta.env.VITE_WORKFORCE_INTAKE_ENABLED === "true"
                    }
                    token={session.access_token}
                    canRecord={
                      Array.isArray(
                        session.user.app_metadata?.sidekick_roles,
                      ) &&
                      session.user.app_metadata.sidekick_roles.some(
                        (role: string) =>
                          ["operator", "governor", "admin"].includes(role),
                      )
                    }
                  />
                </Suspense>
              </RouteBoundary>
            </div>
          )}
          {route === "warriors" && (
            <RouteBoundary route={route}>
              <Suspense
                fallback={
                  <div className="panel center-state" role="status">
                    Loading Working Warriors catalogue…
                  </div>
                }
              >
                <WorkingWarriors />
              </Suspense>
            </RouteBoundary>
          )}
          {route !== "sms" &&
            route !== "warriors" &&
            route !== "opportunity" &&
            !loading &&
            !error &&
            data && (
              <RouteBoundary route={route}>
                {route === "command" && <Command data={data} />}
                {route === "nodes" && <Nodes data={data} />}
                {route === "organizations" && (
                  <Organizations
                    data={data}
                    token={session.access_token}
                    refresh={() => void load()}
                  />
                )}
                {route === "evidence" && <Evidence data={data} />}
              </RouteBoundary>
            )}
        </div>
      </main>
    </div>
  );
}

class RouteBoundary extends Component<
  { route: Route; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("SideKick screen failed to render", {
      route: this.props.route,
      error,
      componentStack: info.componentStack,
    });
  }

  componentDidUpdate(previous: { route: Route }) {
    if (previous.route !== this.props.route && this.state.failed) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="panel error-state route-error" role="alert">
          <strong>This screen could not be displayed</strong>
          <p>
            SideKick kept the rest of the console available. Reload this screen
            or choose another section from the navigation.
          </p>
          <button
            className="button button--outline"
            onClick={() => window.location.reload()}
          >
            Reload screen
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Command({ data }: { data: DashboardReadout }) {
  return (
    <div className="overview-page">
      <section className="overview-intro">
        <div>
          <p className="eyebrow">{data.program.code}</p>
          <h2>Know what is real. Move what is ready.</h2>
          <p>
            SideKick turns approved objectives into bounded work, named
            evidence, and accountable decisions.
          </p>
        </div>
        <span className="status status--active">{data.program.status}</span>
      </section>
      <section className="metric-links" aria-label="Operational summary">
        <MetricLink
          href="#organizations"
          value={data.counts.organizations}
          label="Organizations"
          detail={`${data.counts.organizationsInReview ?? 0} under review`}
        />
        <MetricLink
          href="#organizations"
          value={data.counts.pendingDecisions}
          label="SETC decisions"
          detail="Awaiting accountable action"
          attention={data.counts.pendingDecisions > 0}
        />
        <MetricLink
          href="#nodes"
          value={data.counts.nodes}
          label="Infrastructure nodes"
          detail="Across the canonical pipeline"
        />
        <MetricLink
          href="#evidence"
          value={data.counts.evidence}
          label="Evidence links"
          detail="Referenced in the register"
        />
      </section>
      <div className="overview-workspace">
        <section className="panel attention-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">WORK QUEUE</p>
              <h2>Needs attention</h2>
              <p className="heading-support">
                Recent organizations moving through verification and scope
                gates.
              </p>
            </div>
            <a className="text-link" href="#organizations">
              Open directory <ArrowRight size={14} />
            </a>
          </div>
          {data.organizationQueue.length === 0 ? (
            <Empty
              title="The work queue is clear"
              body="New intake and governance decisions will appear here."
            />
          ) : (
            <div className="attention-list">
              {data.organizationQueue.map((org) => (
                <a href="#organizations" key={org.id}>
                  <span className="record-icon">
                    <Building2 size={16} />
                  </span>
                  <span>
                    <strong>{org.legalName}</strong>
                    <small>{org.jurisdiction}</small>
                  </span>
                  <span className={statusClass(org.state)}>
                    {formatState(org.state)}
                  </span>
                  <ChevronRight size={16} />
                </a>
              ))}
            </div>
          )}
        </section>
        <GovernanceSpine />
      </div>
      <Checkpoints data={data} />
    </div>
  );
}

function GovernanceSpine() {
  const steps = [
    "Observed",
    "Trusted",
    "Modeled",
    "Authorized",
    "Executed",
    "Measured",
    "Recalibrated",
  ];
  return (
    <section className="panel governance">
      <div className="section-heading">
        <div>
          <p className="eyebrow">GOVERNANCE SPINE</p>
          <h2>Evidence to outcome</h2>
        </div>
      </div>
      <ol>
        {steps.map((step, index) => (
          <li key={step}>
            <span>{index + 1}</span>
            <strong>{step}</strong>
            <small>{index < 2 ? "Operating zone" : "Explicit gate"}</small>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Checkpoints({ data }: { data: DashboardReadout }) {
  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">100-DAY CONTROL PLANE</p>
          <h2>Program checkpoints</h2>
        </div>
        <small>Updated {new Date(data.generatedAt).toLocaleString()}</small>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th>Gate</th>
              <th>Phase</th>
              <th>Due</th>
              <th>Work status</th>
            </tr>
          </thead>
          <tbody>
            {data.checkpoints.map((item) => (
              <tr key={item.code}>
                <td className="mono">D{String(item.day).padStart(3, "0")}</td>
                <td>
                  <strong>{item.title}</strong>
                  <small>{item.code}</small>
                </td>
                <td>{checkpointPhase(item.day)}</td>
                <td>{new Date(item.dueAt).toLocaleDateString()}</td>
                <td>
                  <span className="status status--neutral">
                    {item.workStatus ?? "Unassigned"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Nodes({ data }: { data: DashboardReadout }) {
  return (
    <div className="registry-page">
      <section className="page-lead">
        <div>
          <p className="eyebrow">CANONICAL NODE REGISTER</p>
          <h2>Infrastructure, ordered by readiness.</h2>
          <p>
            The node register shows validated infrastructure only. Candidate
            data remains outside the operating register until its source is
            trusted.
          </p>
        </div>
        <div className="lead-stat">
          <strong>{data.counts.nodes.toLocaleString()}</strong>
          <span>validated nodes</span>
        </div>
      </section>
      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">PIPELINE</p>
            <h2>Stage distribution</h2>
          </div>
          <span className="status status--neutral">Read-only register</span>
        </div>
        {data.nodeStages.length === 0 ? (
          <Empty
            icon={<Layers3 size={26} />}
            title="No validated nodes yet"
            body="The register remains at zero until a source inventory is validated and ingested. Evidence requirements can be reviewed now."
            action={{ href: "#evidence", label: "Review evidence model" }}
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lifecycle stage</th>
                  <th>Nodes</th>
                  <th>Capacity</th>
                </tr>
              </thead>
              <tbody>
                {data.nodeStages.map((item) => (
                  <tr key={item.stage}>
                    <td className="mono">{formatState(item.stage)}</td>
                    <td>{item.count.toLocaleString()}</td>
                    <td>{item.capacityMw.toLocaleString()} MW</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Organizations({
  data,
  token,
  refresh,
}: {
  data: DashboardReadout;
  token: string;
  refresh: () => void;
}) {
  const [view, setView] = useState<OrganizationView>("directory");
  const [draftQuery, setDraftQuery] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [result, setResult] = useState<OrganizationQueuePage | null>(null);
  const [selected, setSelected] = useState<OrganizationQueueItem | null>(null);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(draftQuery.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [draftQuery]);

  useEffect(() => {
    const controller = new AbortController();
    setDirectoryLoading(true);
    setDirectoryError(null);
    void api
      .organizations(
        token,
        { q: query, state, category, subcategory, page, pageSize },
        controller.signal,
      )
      .then((response) => {
        setResult(response);
        setSelected((current) =>
          current && response.items.some((item) => item.id === current.id)
            ? current
            : null,
        );
      })
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === "AbortError")) {
          setDirectoryError(
            reason instanceof Error ? reason.message : "Directory unavailable.",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDirectoryLoading(false);
      });
    return () => controller.abort();
  }, [category, page, pageSize, query, reloadKey, state, subcategory, token]);

  const categoryDefinition = ORGANIZATION_TAXONOMY.find(
    (item) => item.code === category,
  );

  const pageCount = Math.max(1, Math.ceil((result?.total ?? 0) / pageSize));
  const rangeStart = result?.total ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, result?.total ?? 0);
  const handleCreated = () => {
    refresh();
    setReloadKey((value) => value + 1);
    setView("directory");
  };

  return (
    <div className="registry-page">
      <section className="page-lead organization-lead">
        <div>
          <p className="eyebrow">PARTICIPATION CONTROL PLANE</p>
          <h2>Identity before access. Scope before execution.</h2>
          <p>
            Find, inspect, and advance organizations through evidence-backed
            verification and explicit SETC decisions.
          </p>
        </div>
        <div className="compact-metrics" aria-label="Organization summary">
          <Metric value={data.counts.organizations} label="In pipeline" />
          <Metric
            value={data.counts.organizationsInReview ?? 0}
            label="Under review"
          />
          <Metric value={data.counts.pendingDecisions} label="SETC decisions" />
          <Metric
            value={data.counts.activeOrganizations ?? 0}
            label="Active scopes"
          />
        </div>
      </section>
      <div
        className="view-switcher"
        role="tablist"
        aria-label="Organizations view"
      >
        <button
          role="tab"
          aria-selected={view === "directory"}
          className={view === "directory" ? "active" : ""}
          onClick={() => setView("directory")}
        >
          <Building2 size={16} /> Directory
        </button>
        <button
          role="tab"
          aria-selected={view === "intake"}
          className={view === "intake" ? "active" : ""}
          onClick={() => setView("intake")}
        >
          <Plus size={16} /> Add organization
        </button>
      </div>
      {view === "intake" ? (
        <OrganizationForm token={token} onCreated={handleCreated} />
      ) : (
        <section className="panel directory-panel">
          <div className="directory-toolbar">
            <div className="search-field">
              <Search size={17} />
              <label htmlFor="organization-search" className="sr-only">
                Search organizations
              </label>
              <input
                id="organization-search"
                type="search"
                value={draftQuery}
                placeholder="Search name, jurisdiction, or role"
                onChange={(event) => setDraftQuery(event.target.value)}
              />
              {draftQuery && (
                <button
                  type="button"
                  className="clear-search"
                  aria-label="Clear search"
                  onClick={() => setDraftQuery("")}
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <label className="toolbar-field">
              <span>Category</span>
              <select
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setSubcategory("");
                  setPage(1);
                }}
              >
                <option value="">All categories</option>
                {ORGANIZATION_TAXONOMY.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="toolbar-field">
              <span>Subcategory</span>
              <select
                value={subcategory}
                disabled={!categoryDefinition}
                onChange={(event) => {
                  setSubcategory(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">All subcategories</option>
                {categoryDefinition?.subcategories.map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="toolbar-field">
              <span>State</span>
              <select
                value={state}
                onChange={(event) => {
                  setState(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">All states</option>
                {ORG_INTEGRATION_STATES.map((item) => (
                  <option key={item} value={item}>
                    {formatState(item)}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="button button--primary"
              onClick={() => setView("intake")}
            >
              <Plus size={15} /> Add organization
            </button>
          </div>
          {directoryLoading && !result ? (
            <div className="center-state" role="status">
              <span className="spinner" /> Loading organization directory…
            </div>
          ) : directoryError ? (
            <div className="error-state" role="alert">
              <strong>Organization directory unavailable</strong>
              <p>{directoryError}</p>
              <button
                className="button button--outline"
                onClick={() => setReloadKey((value) => value + 1)}
              >
                Retry
              </button>
            </div>
          ) : result?.items?.length === 0 &&
            (query || state || category || subcategory) ? (
            <Empty
              title="No matching organizations"
              body="Change or clear the filters to broaden the directory."
            />
          ) : result?.items?.length === 0 ? (
            <Empty
              title="No organizations yet"
              body="Record an organization to begin controlled verification and scope review."
              action={{
                onClick: () => setView("intake"),
                label: "Add organization",
              }}
            />
          ) : (
            <>
              <div className="directory-summary" aria-live="polite">
                <span>
                  Showing {rangeStart.toLocaleString()}–
                  {rangeEnd.toLocaleString()} of{" "}
                  {(result?.total ?? 0).toLocaleString()}
                </span>
                {directoryLoading && <span>Updating…</span>}
              </div>
              <div className="table-wrap directory-table">
                <table>
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th>Jurisdiction</th>
                      <th>Verification</th>
                      <th>State</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {result?.items?.map((org) => (
                      <OrganizationRow
                        key={org.id}
                        organization={org}
                        selected={selected?.id === org.id}
                        onInspect={() => setSelected(org)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                <label className="toolbar-field">
                  <span>Rows</span>
                  <select
                    value={pageSize}
                    onChange={(event) => {
                      setPageSize(Number(event.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <span>
                  Page {page.toLocaleString()} of {pageCount.toLocaleString()}
                </span>
                <div>
                  <button
                    className="icon-button bordered"
                    aria-label="Previous page"
                    disabled={page <= 1}
                    onClick={() => setPage((value) => Math.max(1, value - 1))}
                  >
                    <ChevronLeft size={17} />
                  </button>
                  <button
                    className="icon-button bordered"
                    aria-label="Next page"
                    disabled={page >= pageCount}
                    onClick={() => setPage((value) => value + 1)}
                  >
                    <ChevronRight size={17} />
                  </button>
                </div>
              </div>
            </>
          )}
          {selected && (
            <OrganizationDetail
              organization={selected}
              onClose={() => setSelected(null)}
            />
          )}
        </section>
      )}
    </div>
  );
}

function OrganizationRow({
  organization,
  selected,
  onInspect,
}: {
  organization: OrganizationQueueItem;
  selected: boolean;
  onInspect: () => void;
}) {
  const passed = organization.passedCheckCount ?? 0;
  const checks = organization.checkCount ?? 0;
  const progress = checks ? Math.round((passed / checks) * 100) : 0;
  return (
    <tr className={selected ? "selected-row" : undefined}>
      <td>
        <strong>{organization.legalName}</strong>
        <small>
          {organization.subcategoryName || organization.requestedRole}
        </small>
      </td>
      <td>{organization.jurisdiction || "Pending"}</td>
      <td>
        <div className="row-progress">
          <span>{checks ? `${passed}/${checks}` : "Not started"}</span>
          <div className="progress-track" aria-label={`${progress}% verified`}>
            <span style={{ width: `${progress}%` }} />
          </div>
        </div>
      </td>
      <td>
        <span className={statusClass(organization.state)}>
          {formatState(organization.state)}
        </span>
      </td>
      <td>
        <button className="text-button" onClick={onInspect}>
          Inspect
        </button>
      </td>
    </tr>
  );
}

function OrganizationDetail({
  organization,
  onClose,
}: {
  organization: OrganizationQueueItem;
  onClose: () => void;
}) {
  return (
    <aside
      className="record-detail"
      aria-label={`${organization.legalName} details`}
    >
      <div className="section-heading">
        <div>
          <p className="eyebrow">SELECTED RECORD</p>
          <h2>{organization.legalName}</h2>
          <p className="heading-support">
            Updated {new Date(organization.updatedAt).toLocaleString()}
          </p>
        </div>
        <button
          className="icon-button bordered"
          aria-label="Close details"
          onClick={onClose}
        >
          <X size={17} />
        </button>
      </div>
      <dl className="record-facts">
        <div>
          <dt>Category</dt>
          <dd>{organization.primaryCategoryName}</dd>
        </div>
        <div>
          <dt>Subcategory</dt>
          <dd>{organization.subcategoryName}</dd>
        </div>
        <div>
          <dt>Relationship</dt>
          <dd>{formatState(organization.relationshipType)}</dd>
        </div>
        <div>
          <dt>Jurisdiction</dt>
          <dd>{organization.jurisdiction}</dd>
        </div>
        <div>
          <dt>Requested role</dt>
          <dd>{organization.requestedRole}</dd>
        </div>
        <div>
          <dt>Active scopes</dt>
          <dd>{organization.activeScopeCount ?? 0}</dd>
        </div>
      </dl>
      <div className="next-gate next-gate--informational">
        <span>
          <Clock3 size={14} /> Next gate
        </span>
        <strong>{nextOrganizationGate(organization.state)}</strong>
        <small>
          Workflow action will appear when the governed review service is
          connected.
        </small>
      </div>
    </aside>
  );
}

function Evidence({ data }: { data: DashboardReadout }) {
  return (
    <div className="registry-page">
      <section className="page-lead">
        <div>
          <p className="eyebrow">EVIDENCE LINEAGE</p>
          <h2>One reference. Clear custody.</h2>
          <p>
            SideKick points to approved evidence without turning operational
            data into a second document repository.
          </p>
        </div>
        <div className="lead-stat">
          <strong>{data.counts.evidence.toLocaleString()}</strong>
          <span>registered links</span>
        </div>
      </section>
      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">CUSTODY MODEL</p>
            <h2>Referenced, not duplicated</h2>
          </div>
          <span className="status status--active">Governed lineage</span>
        </div>
        <div className="evidence-principles">
          <Principle
            icon={<Database />}
            title="Supabase"
            text="Operational metadata, checkpoints, and append-only integration events."
          />
          <Principle
            icon={<FileCheck2 />}
            title="Drive"
            text="Approved documents, evidence packages, and human-readable records."
          />
          <Principle
            icon={<ShieldCheck />}
            title="Authority"
            text="Verification and authorization remain with competent reviewers and SETC."
          />
        </div>
        {data.counts.evidence === 0 && (
          <Empty
            title="No evidence links registered"
            body="Evidence enters the register through a governed workflow. Start with an organization intake and its declared evidence location."
            action={{ href: "#organizations", label: "Open organizations" }}
          />
        )}
      </section>
    </div>
  );
}

function MetricLink({
  href,
  value,
  label,
  detail,
  attention = false,
}: {
  href: string;
  value: number;
  label: string;
  detail: string;
  attention?: boolean;
}) {
  return (
    <a href={href} className={attention ? "attention" : undefined}>
      <span>
        <strong>{value.toLocaleString()}</strong>
        <small>{label}</small>
      </span>
      <p>{detail}</p>
      <ArrowRight size={16} />
    </a>
  );
}

function Metric({ value, label }: { value: number; label: string }) {
  return (
    <div className="metric">
      <strong>{value.toLocaleString()}</strong>
      <span>{label}</span>
    </div>
  );
}

function Empty({
  icon,
  title,
  body,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  body: string;
  action?: { label: string; href?: string; onClick?: () => void };
}) {
  return (
    <div className="empty-state">
      {icon && <span className="empty-icon">{icon}</span>}
      <strong>{title}</strong>
      <p>{body}</p>
      {action?.href ? (
        <a className="button button--outline button-link" href={action.href}>
          {action.label}
        </a>
      ) : action ? (
        <button className="button button--outline" onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
    </div>
  );
}

function Principle({
  icon,
  title,
  text,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return (
    <article>
      {icon}
      <div>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </article>
  );
}

function checkpointPhase(day: number) {
  if (day === 100) return "Handoff";
  if (day >= 70) return "Qualify";
  if (day >= 30) return "Normalize";
  return "Commission";
}

function formatState(value: string | null | undefined) {
  if (!value) return "Unspecified";
  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

function statusClass(state: string | null | undefined) {
  if (state === "ACTIVE") return "status status--active";
  if (state === "SUSPENDED" || state === "OFFBOARDED")
    return "status status--danger";
  return "status status--pending";
}

function nextOrganizationGate(state: string | null | undefined) {
  switch (state) {
    case "INTAKE":
      return "Open verification";
    case "VERIFICATION":
      return "Complete evidence checks";
    case "SCOPE_REVIEW":
      return "Define bounded scopes";
    case "PENDING_SETC_APPROVAL":
      return "Record SETC decision";
    case "ACTIVE":
      return "Monitor scope validity";
    case "SUSPENDED":
      return "Resolve suspension";
    case "OFFBOARDED":
      return "Retain audit record";
    default:
      return "Review intake";
  }
}

export function App() {
  return (
    <ToastProvider>
      <AuthGate>{(session) => <Console session={session} />}</AuthGate>
    </ToastProvider>
  );
}
